import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { noop } from 'lodash-es'
import { DealActions, DealDetail, DealStatus, NegotiationFieldMeta, RepoClassification, Severity, TradingType, UserPreference } from 'api'
import { Groupbox, ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, NumericInputField, TextInputField } from 'view/components/inputs'
import {
  BooleanSelectboxField,
  DecisionMakerSelectboxField,
  FilteredTermSelectboxField,
  TermSelectboxField,
  TradingDatePickerField
} from '../entity'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { ToggleField } from 'view/components/toggle'
import { HaircutTypeSelectboxField } from '../entity/haircutOptions/HaircutTypeSelectbox'
import {
  canEditTraderAmendStartConsiderationOverride,
  getCusip,
  getFeeFormatOptions,
  getHaircutFormatOptions,
  getIsin,
  getNotionalFormatOptions,
  getRateFormatOptions,
  getStartConsiderationFormatOptions
} from '../datagrid'
import { getCollateralMarket, getDateEditorFormat } from 'model'
import { createDealDetailSelector } from 'selectors/dealSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { Locale, Translation } from 'locale'
import { DealFieldNames } from 'view/routes/deals/components/DealsGridColumnNames'
import {
  canServicingRevertAttributes,
  canServicingSend,
  getDealModificationColour,
  getNegotiatedValue
} from 'view/routes/deals/components/utils'
import {
  getBondPriceModificationType,
  getEndDateModificationType,
  getHaircutModificationType,
  getNotionalModificationType,
  getRateModificationType,
  getStartConsiderationModificationType,
  getStartConsiderationOverrideModificationType,
  getStartDateModificationType,
  getStringModificationType,
  getTermModificationType
} from 'view/routes/requests/shared/deltaGetters'
import * as constants from 'view/styles/constants'
import { NotificationType } from 'store/notification'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { startConsiderationOverrideEnabledSelector, isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { swallowPrecisionError } from 'utils/Decimal'
import { createHandleStartConsiderationOverrideUpdate } from './utils'
import { ServicingSecondaryCollateralComboboxField } from '../entity/SecondaryCollateralSelectbox'
import { isDealDetailSBL } from 'model/dealUtils'
import { useOnMount } from 'view/hooks'

export type ServicingTraderAmendDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  dealId: string
  onClose?: () => unknown
}

interface StateProps {
  dealDetail: DealDetail | undefined
  userPreference: UserPreference
  startConsiderationOverrideEnabled: boolean
  isSBLEnabled: boolean
}

interface DispatchProps {
  onStartTraderAmend: typeof DealActions.servicingStartTraderAmend
  onUpdateField: typeof DealActions.servicingUpdateAttributes
  onRevert: typeof DealActions.servicingRevertAttributes
  onSend: typeof DealActions.servicingSend
  onCancel: typeof DealActions.servicingCancel
}

const dealIdSelector = (_: {}, props: ServicingTraderAmendDialogProps) => props.dealId

const mapStateToProps = createStructuredSelector({
  dealDetail: createDealDetailSelector(dealIdSelector),
  userPreference: createUserPreferenceSelector(),
  startConsiderationOverrideEnabled: startConsiderationOverrideEnabledSelector,
  isSBLEnabled: isStockBorrowingLendingEnabledSelector
})

const mapDispatchToProps = {
  onStartTraderAmend: DealActions.servicingStartTraderAmend,
  onUpdateField: DealActions.servicingUpdateAttributes,
  onRevert: DealActions.servicingRevertAttributes,
  onCancel: DealActions.servicingCancel,
  onSend: DealActions.servicingSend
}

export const ServicingTraderAmendDialogComponent: React.FunctionComponent<ServicingTraderAmendDialogProps> = props => {
  const {
    dealId,
    dealDetail,
    onClose,
    userPreference,
    onSend,
    onUpdateField,
    onStartTraderAmend,
    onCancel,
    showDialog,
    startConsiderationOverrideEnabled,
    isSBLEnabled
  } = props

  useOnMount(() => {
    if (dealDetail?.deal.status !== DealStatus.SERVICING_WITH_INITIATOR) {
      onStartTraderAmend(dealId)
    }
  })

  if (!dealDetail) return null

  const { deal, collateral, referenceRate, servicingSideDetails, ownOrder } = dealDetail

  const market = collateral && getCollateralMarket(collateral)

  const negotiationFields = servicingSideDetails?.negotiationFields
  const sideFields = servicingSideDetails?.sideFields

  const notionalFormatOptions = getNotionalFormatOptions(userPreference.amountFormat)
  const rateFormatOptions = getRateFormatOptions(referenceRate)
  const haircutFormatOptions = getHaircutFormatOptions(undefined, userPreference.haircutSign, ownOrder, deal.direction)
  const dateEditorFormat = getDateEditorFormat(userPreference.dateFormat)
  const currency = dealDetail.deal.currency

  const handleStartConsiderationOverrideUpdate = createHandleStartConsiderationOverrideUpdate(userPreference, dealId, onUpdateField)

  const handleUpdateField = (value: any, field: NegotiationFieldMeta, rawValue?: any) => {
    onUpdateField({
      dealId,
      field,
      updatedValue: value,
      updatedRawValue: rawValue,
      decisionMakerOrientedByNegotiationSide: true,
      meta: { haircutSign: userPreference.haircutSign },
      revertFirst: false
    })
  }

  const handleSend = () => {
    onSend(dealId)
    onClose?.()
  }

  const handleCancel = async () => {
    if (canServicingRevertAttributes(dealDetail)) {
      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.TC_STOP_AMEND_DEAL,
        message: Translation.SC_CLOSE_TICKET_WITH_AMENDMENTS,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }
      const dialogResult = await showDialog(dialogArgs)

      if (dialogResult === Translation.TC_BUTTON_YES) {
        onCancel(dealDetail.deal.dealId)
        onClose?.()
      }
    } else {
      onCancel(dealDetail.deal.dealId)
      onClose?.()
    }
  }

  const validationIssues = servicingSideDetails?.validationIssues ?? []
  const hasValidationErrors = validationIssues.filter(validationIssue => validationIssue.severity === Severity.ERROR).length > 0

  const dealIsNotSbl = !isDealDetailSBL(dealDetail)

  return (
    <ModalWindow title={Locale.translate(Translation.TC_AMEND_TRADE)} onEscKey={handleCancel}>
      <ModalContent>
        <Groupbox>
          <DialogRow>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_DEAL_ID)} value={dealId} readOnly={true} onUpdate={noop} />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_COLLATERAL)}
                value={collateral?.name}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_ISIN_BUI)}
                readOnly={true}
                onUpdate={noop}
                value={getIsin(dealDetail)}
              />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_CUSIP)} readOnly={true} onUpdate={noop} value={getCusip(dealDetail)} />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat })}
                formatOptions={notionalFormatOptions}
                value={deal.notional}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_START_CONSIDERATION)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={{ minDp: 2 }}
                value={deal.startConsideration}
              />
            </DialogField>

            {startConsiderationOverrideEnabled && (
              <DialogField>
                <BooleanSelectboxField
                  label={Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE)}
                  readOnly={true}
                  onUpdate={noop}
                  value={deal.startConsiderationOverride}
                />
              </DialogField>
            )}
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_RATE)}
                formatOptions={rateFormatOptions}
                value={deal.rate}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_HAIRCUT)}
                formatOptions={haircutFormatOptions}
                value={deal.haircut}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField>
              <HaircutTypeSelectboxField
                label={Locale.translate(Translation.TC_HAIRCUT_TYPE)}
                value={deal.haircutType}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COLLATERAL_PRICE)}
                value={deal.bondPrice}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField>
              <TermSelectboxField label={Locale.translate(Translation.TC_TERM)} readOnly={true} onUpdate={noop} value={deal.term} />
            </DialogField>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_START_DATE)}
                market={market}
                readOnly={true}
                dateFormat={dateEditorFormat}
                onUpdate={noop}
                value={deal.startDate}
              />
            </DialogField>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_END_DATE)}
                readOnly={true}
                dateFormat={dateEditorFormat}
                onUpdate={noop}
                value={deal.endDate}
              />
            </DialogField>
          </DialogRow>
          {isSBLEnabled ? (
            <DialogRow>
              <DialogField>
                <NumericInputField label={Locale.translate(Translation.TC_FEE)} readOnly={true} onUpdate={noop} value={deal.fee} />
              </DialogField>

              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_STOCK_BORROW_LENDING_TYPE)}
                  readOnly={true}
                  onUpdate={noop}
                  value={deal.stockBorrowingLendingType}
                />
              </DialogField>

              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_SECONDARY_COLLATERAL)}
                  onUpdate={noop}
                  readOnly={true}
                  value={dealDetail.secondaryInstrumentName}
                />
              </DialogField>
            </DialogRow>
          ) : null}
        </Groupbox>
        <Groupbox>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_NOTIONAL, { amountFormat: userPreference.amountFormat })}
                fieldName={NegotiationFieldMeta.NOTIONAL}
                validationField={DealFieldNames.DEAL_NOTIONAL}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getNotionalModificationType(negotiationFields?.NOTIONAL?.negotiableFieldRequired),
                  dealDetail
                )}
                min={collateral?.ticketIncrement}
                step={collateral?.ticketIncrement}
                formatOptions={notionalFormatOptions}
                onUpdate={handleUpdateField}
                value={getNegotiatedValue(negotiationFields?.NOTIONAL?.negotiableFieldRequired)}
                dataTestId={'ServicingTraderAmendDialog-NumericInputField-Notional'}
                readOnly={deal.tradingType !== TradingType.NOTIONAL}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_START_CONSIDERATION)}
                fieldName={NegotiationFieldMeta.START_CONSIDERATION}
                validationField={DealFieldNames.DEAL_START_CONSIDERATION}
                validationIssues={validationIssues}
                underline={swallowPrecisionError(getDealModificationColour)(
                  () => getStartConsiderationModificationType(negotiationFields?.START_CONSIDERATION?.negotiableFieldRequired),
                  dealDetail
                )}
                min={collateral?.ticketIncrement}
                step={collateral?.ticketIncrement}
                formatOptions={getStartConsiderationFormatOptions()}
                onUpdate={handleUpdateField}
                value={getNegotiatedValue(negotiationFields?.START_CONSIDERATION?.negotiableFieldRequired)}
                dataTestId={'ServicingTraderAmendDialog-NumericInputField-StartConsideration'}
                readOnly={deal.tradingType !== TradingType.CASH}
              />
            </DialogField>
            {startConsiderationOverrideEnabled && (
              <DialogField>
                <BooleanSelectboxField
                  label={Locale.translate(Translation.TC_NEW_START_CONSIDERATION_OVERRIDE)}
                  fieldName={NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE}
                  validationField={DealFieldNames.DEAL_START_CONSIDERATION_OVERRIDE}
                  validationIssues={validationIssues}
                  underline={swallowPrecisionError(getDealModificationColour)(
                    () => getStartConsiderationOverrideModificationType(negotiationFields?.START_CONSIDERATION?.negotiableFieldRequired),
                    dealDetail
                  )}
                  onUpdate={handleStartConsiderationOverrideUpdate}
                  value={getNegotiatedValue(negotiationFields?.START_CONSIDERATION_OVERRIDE?.negotiableFieldRequired)}
                  dataTestId={'ServicingTraderAmendDialog-BooleanInputField-StartConsiderationOverride'}
                  readOnly={canEditTraderAmendStartConsiderationOverride(dealDetail)}
                  updateOnlyOnChangedValue={true}
                />
              </DialogField>
            )}
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_RATE)}
                fieldName={NegotiationFieldMeta.RATE}
                validationField={DealFieldNames.DEAL_RATE}
                underline={getDealModificationColour(
                  () => getRateModificationType(negotiationFields?.RATE?.negotiableFieldRequired),
                  dealDetail
                )}
                formatOptions={rateFormatOptions}
                validationIssues={validationIssues}
                value={getNegotiatedValue(negotiationFields?.RATE?.negotiableFieldRequired)}
                onUpdate={handleUpdateField}
                readOnly={!dealIsNotSbl}
                dataTestId={'ServicingTraderAmendDialog-NumericInputField-Rate'}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_HAIRCUT)}
                fieldName={NegotiationFieldMeta.HAIRCUT}
                validationField={DealFieldNames.DEAL_HAIRCUT}
                formatOptions={haircutFormatOptions}
                min={0}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getHaircutModificationType(negotiationFields?.HAIRCUT?.negotiableFieldRequired),
                  dealDetail
                )}
                value={getNegotiatedValue(negotiationFields?.HAIRCUT?.negotiableFieldRequired)}
                onUpdate={handleUpdateField}
                dataTestId={'ServicingTraderAmendDialog-NumericInputField-Haircut'}
              />
            </DialogField>
            <DialogField>
              <HaircutTypeSelectboxField
                label={Locale.translate(Translation.TC_NEW_HAIRCUT_TYPE)}
                fieldName={NegotiationFieldMeta.HAIRCUT_TYPE}
                validationField={DealFieldNames.DEAL_HAIRCUT_TYPE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getStringModificationType(negotiationFields?.HAIRCUT_TYPE?.negotiableFieldRequired),
                  dealDetail
                )}
                value={getNegotiatedValue(negotiationFields?.HAIRCUT_TYPE?.negotiableFieldRequired)}
                onUpdate={handleUpdateField}
                canClear={true}
                dataTestId={'ServicingTraderAmendDialog-NumericInputField-HaircutType'}
              />
            </DialogField>
          </DialogRow>
        </Groupbox>
        <Groupbox>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_COLLATERAL_PRICE)}
                fieldName={NegotiationFieldMeta.BOND_PRICE}
                validationField={DealFieldNames.DEAL_BOND_PRICE}
                validationIssues={validationIssues}
                underline={swallowPrecisionError(getDealModificationColour)(
                  () => getBondPriceModificationType(negotiationFields?.BOND_PRICE?.negotiableFieldRequired),
                  dealDetail
                )}
                min={0}
                maxLength={constants.bondPriceMaxFieldLength}
                formatOptions={{
                  maxFractionDigits: constants.bondPriceMaxFractionDigits
                }}
                onUpdate={handleUpdateField}
                value={getNegotiatedValue(negotiationFields?.BOND_PRICE?.negotiableFieldRequired)}
                dataTestId={'ServicingTraderAmendDialog-NumericInputField-BondPrice'}
              />
            </DialogField>
            <DialogField>
              <FilteredTermSelectboxField
                label={Locale.translate(Translation.TC_NEW_TERM)}
                fieldName={NegotiationFieldMeta.TERM}
                validationField={DealFieldNames.DEAL_TERM}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getTermModificationType(negotiationFields?.TERM?.negotiableFieldRequired),
                  dealDetail
                )}
                onUpdate={handleUpdateField}
                repoClassification={deal.repoClassification}
                value={getNegotiatedValue(negotiationFields?.TERM?.negotiableFieldRequired)}
                dataTestId={'ServicingTraderAmendDialog-NumericInputField-Term'}
              />
            </DialogField>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_NEW_START_DATE)}
                market={market}
                fieldName={NegotiationFieldMeta.START_DATE}
                validationField={DealFieldNames.DEAL_START_DATE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getStartDateModificationType(negotiationFields?.START_DATE?.negotiableFieldRequired),
                  dealDetail
                )}
                dateFormat={dateEditorFormat}
                onUpdate={(value: string | null) => handleUpdateField(value, NegotiationFieldMeta.START_DATE)}
                value={getNegotiatedValue(negotiationFields?.START_DATE?.negotiableFieldRequired)}
                dataTestId={'ServicingTraderAmendDialog-NumericInputField-StartDate'}
              />
            </DialogField>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_NEW_END_DATE)}
                fieldName={DealFieldNames.DEAL_END_DATE}
                validationIssues={validationIssues}
                validationField={DealFieldNames.DEAL_END_DATE}
                underline={getDealModificationColour(
                  () => getEndDateModificationType(negotiationFields?.END_DATE?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={deal.repoClassification === RepoClassification.OPEN}
                dateFormat={dateEditorFormat}
                onUpdate={(value: string | null) => handleUpdateField(value, NegotiationFieldMeta.END_DATE)}
                value={getNegotiatedValue(negotiationFields?.END_DATE?.negotiableFieldRequired)}
                dataTestId={'ServicingTraderAmendDialog-NumericInputField-EndDate'}
              />
            </DialogField>
          </DialogRow>
          {isSBLEnabled ? (
            <DialogRow>
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_NEW_STOCK_BORROW_LENDING_TYPE)}
                  readOnly={true}
                  onUpdate={noop}
                  value={deal.stockBorrowingLendingType}
                />
              </DialogField>
              <DialogField>
                <NumericInputField
                  label={Locale.translate(Translation.TC_NEW_FEE)}
                  fieldName={NegotiationFieldMeta.FEE}
                  validationField={DealFieldNames.DEAL_FEE}
                  validationIssues={validationIssues}
                  underline={getDealModificationColour(
                    () => getRateModificationType(negotiationFields?.FEE?.negotiableFieldRequired),
                    dealDetail
                  )}
                  readOnly={dealIsNotSbl}
                  formatOptions={getFeeFormatOptions()}
                  onUpdate={handleUpdateField}
                  value={getNegotiatedValue(negotiationFields?.FEE?.negotiableFieldRequired)}
                  dataTestId={'DealTicketFields-NumericInputField-Fee'}
                />
              </DialogField>

              <DialogField>
                <ServicingSecondaryCollateralComboboxField
                  detail={dealDetail}
                  fieldName={NegotiationFieldMeta.SECONDARY_BASKET_ID}
                  label={Locale.translate(Translation.TC_NEW_SECONDARY_COLLATERAL)}
                  onUpdate={handleUpdateField}
                  underline={getDealModificationColour(
                    () => getTermModificationType(negotiationFields?.SECONDARY_BASKET_ID?.negotiableFieldRequired),
                    dealDetail
                  )}
                  validationIssues={validationIssues}
                  validationField={DealFieldNames.DEAL_SECONDARY_COLLATERAL}
                  readOnly={dealIsNotSbl}
                  value={getNegotiatedValue(negotiationFields?.SECONDARY_BASKET_ID?.negotiableFieldRequired)}
                />
              </DialogField>
            </DialogRow>
          ) : null}
        </Groupbox>

        <Groupbox>
          <DialogRow verticalLayout={constants.VerticalLayout.Center}>
            <DialogField>
              <ToggleField
                dataTestIdField={'ServicingTraderAmendDialog-ToggleField-PreAgreed'}
                fieldName={NegotiationFieldMeta.PRE_AGREED}
                checked={getNegotiatedValue(negotiationFields?.PRE_AGREED?.negotiableFieldRequired)}
                onUpdate={handleUpdateField}
                label={Locale.translate(Translation.TC_PRE_AGREED)}
              />
            </DialogField>
            <DialogField>
              <DecisionMakerSelectboxField
                label={Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER)}
                fieldName={
                  ownOrder
                    ? NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER
                    : NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER
                }
                validationField={
                  ownOrder
                    ? DealFieldNames.DEAL_INITIATOR_INVESTMENT_DECISION_MAKER
                    : DealFieldNames.DEAL_RESPONDER_INVESTMENT_DECISION_MAKER
                }
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () =>
                    getStringModificationType(
                      sideFields?.[ownOrder ? 'INITIATOR_INVESTMENT_DECISION_MAKER' : 'RESPONDER_INVESTMENT_DECISION_MAKER']
                        ?.negotiableFieldRequired
                    ),
                  dealDetail
                )}
                value={getNegotiatedValue(
                  sideFields?.[ownOrder ? 'INITIATOR_INVESTMENT_DECISION_MAKER' : 'RESPONDER_INVESTMENT_DECISION_MAKER']
                    ?.negotiableFieldRequired
                )}
                onUpdate={handleUpdateField}
                dataTestId={'ServicingSubstitutionDialog-DecisionMakerSelectboxField-Investment'}
              />
            </DialogField>
            <DialogField>
              <DecisionMakerSelectboxField
                label={Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER)}
                fieldName={
                  ownOrder
                    ? NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER
                    : NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER
                }
                validationField={
                  ownOrder ? DealFieldNames.DEAL_INITIATOR_EXECUTION_DECISION_MAKER : DealFieldNames.DEAL_RESPONDER_EXECUTION_DECISION_MAKER
                }
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () =>
                    getStringModificationType(
                      sideFields?.[ownOrder ? 'INITIATOR_EXECUTION_DECISION_MAKER' : 'RESPONDER_EXECUTION_DECISION_MAKER']
                        ?.negotiableFieldRequired
                    ),
                  dealDetail
                )}
                value={getNegotiatedValue(
                  sideFields?.[ownOrder ? 'INITIATOR_EXECUTION_DECISION_MAKER' : 'RESPONDER_EXECUTION_DECISION_MAKER']
                    ?.negotiableFieldRequired
                )}
                onUpdate={handleUpdateField}
                dataTestId={'ServicingSubstitutionDialog-DecisionMakerSelectboxField-Execution'}
              />
            </DialogField>
          </DialogRow>
        </Groupbox>
      </ModalContent>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={handleCancel} dataTestId={'ServicingDialog-CancelButton'}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              type={TextButtonType.Primary}
              disabled={hasValidationErrors || !canServicingRevertAttributes(dealDetail) || !canServicingSend(dealDetail)}
              onClick={handleSend}
              dataTestId={'ServicingTraderAmendDialog-SendRequestButton'}>
              {Locale.translate(Translation.TC_BUTTON_SEND_REQUEST)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const ServicingTraderAmendDialog = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(ServicingTraderAmendDialogComponent)
)
